import { State } from '@sbercloud/uikit-product-calculator';
import { Typography } from '@snack-uikit/typography';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { useOnClickOutside } from 'src/hooks/useOnClickOutside';
import { useSubmitForm } from 'src/hooks/useSubmitForm';
import { useTryFormErrorsCheck } from 'src/hooks/useTryFormErrorsCheck';
import { Checkbox } from 'src/snack/components/Form/Checkbox';
import { FormNoticeBlock } from 'src/snack/components/Form/FormNotice';
import { Input } from 'src/snack/components/Form/Input';
import { Submit } from 'src/snack/components/Form/Submit';
import { TextArea } from 'src/snack/components/Form/TextArea';
import { UserAgreementCheckboxes } from 'src/snack/components/Form/UserAgreementCheckboxes';
import { DataFromFormInputs } from 'src/types';
import { ResultCodes } from 'src/uikit/Form/constants';

import s from './Form.module.scss';

function beAdapter(state: State) {
  const stateForBe = state;
  return stateForBe;
}

const formVariant = 'modal';

const agreementName = 'site_services_consent';

const formSuccessNotice = {
  title: 'Мы приняли вашу заявку, скоро с вами свяжется специалист.',
};

function Form({ modalClose, state }: { modalClose: () => void; state: State }) {
  const layoutType = useLayoutType();
  const [showNotice, setShowNotice] = useState(true);
  const ref = useRef<HTMLFormElement | null>(null);
  const { submitForm, formStatus, setFormStatus, formMessage, resultCode } =
    useSubmitForm();
  const { successfullSubmissionFormAnalytics } = useAnalytics();

  const formMethods = useForm<DataFromFormInputs>({
    mode: 'onChange',
    defaultValues: {
      userAgreement: true,
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setError,
  } = formMethods;

  useOnClickOutside(ref, modalClose);
  useTryFormErrorsCheck(errors, isSubmitting, 'calculator');

  useEffect(() => {
    if (
      formStatus === 'error' &&
      resultCode === ResultCodes.EmailAlreadyExist
    ) {
      setError('email', {
        type: 'manual',
        message: formMessage,
      });
      setShowNotice(false);
    } else {
      setShowNotice(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStatus]);

  const isPhysic = !!watch('isNaturalPerson', false);

  const isSendingStatus = formStatus === 'sending';

  const isFormHidden =
    layoutType === 'mobile' &&
    formStatus !== 'idle' &&
    formStatus !== 'sending' &&
    showNotice;

  async function onSubmit(data: DataFromFormInputs) {
    const stateOfCalc = JSON.parse(JSON.stringify(state));
    const stateForBe = beAdapter(stateOfCalc);
    const other_consents = data.site_services_consent
      ? ['site_services_consent']
      : ['online_event_consent'];

    if (data.site_services_consent) {
      delete data.site_services_consent;
    } else {
      delete data.online_event_consent;
    }

    const dataToSend = {
      ...data,
      ...(data?.isNaturalPerson && typeof data.isNaturalPerson === 'string'
        ? { isNaturalPerson: JSON.parse(data.isNaturalPerson) }
        : {}),
      ...(data?.subscribeForMailing &&
      typeof data.subscribeForMailing === 'string'
        ? { subscribeForMailing: JSON.parse(data.subscribeForMailing) }
        : {}),
      other_consents,
      calcData: stateForBe,
      userAgreement: true,
    };

    await submitForm({
      backendPatchInput: '/forms/v1/consultation_calc',
      dataFromFormInputs: dataToSend,
      allFormSentFormType: 'calculator',
      onSuccess: () => {
        successfullSubmissionFormAnalytics({
          zoneclick: 'B',
          formname: 'calculator',
        });

        setFormStatus('idle');
        reset();
      },
    });
  }

  const formErrorNotice = {
    title: formMessage,
    buttonTitle: 'Обновить страницу',
  };

  return (
    <div className={s.formWithNotice}>
      {!isFormHidden && (
        <div
          className={s.formWrapper}
          data-modal={formVariant === 'modal' || undefined}
          data-layout-type={layoutType}
        >
          <FormProvider {...formMethods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              data-test-id="section_form"
              data-layout-type={layoutType}
              className={cn(s.form, { [s.isSending]: isSendingStatus })}
            >
              <div className={s.header}>
                <Typography family="sans" purpose="headline" size="l" tag="h2">
                  Связаться с нашим специалистом
                </Typography>
              </div>
              <div className={s.double} data-layout={layoutType}>
                <Input
                  name="name"
                  label={'Имя'}
                  inputType="text"
                  validationType={Input.validations.Name}
                  required
                  disabled={isSendingStatus}
                  className={s.input}
                />
                <Input
                  name="phone"
                  inputType="phone"
                  label={'Телефон'}
                  required
                  disabled={isSendingStatus}
                  className={s.input}
                />
              </div>

              <div data-layout={layoutType}>
                <Input
                  name="email"
                  inputType="text"
                  validationType={Input.validations.Email}
                  label={'Электронная почта'}
                  required
                  disabled={isSendingStatus}
                  className={s.input}
                />
              </div>

              <Checkbox
                className={s.checkbox}
                required={false}
                disabled={isSendingStatus}
                name="isNaturalPerson"
                value="true"
                label={'Я физлицо'}
              />
              <div className={s.double} data-layout={layoutType}>
                <Input
                  name="company"
                  validationType={Input.validations.Company}
                  inputType="text"
                  label={'Название компании'}
                  required
                  disabled={isPhysic || isSendingStatus}
                  className={s.input}
                />
                <Input
                  name="position"
                  validationType={Input.validations.Position}
                  inputType="text"
                  label={'Должность'}
                  disabled={isPhysic || isSendingStatus}
                  required
                  className={s.input}
                />
              </div>

              <TextArea
                label={'Комментарий'}
                name="comment"
                className={s.textarea}
                disabled={isSendingStatus}
              />

              <UserAgreementCheckboxes
                agreementName={agreementName}
                sendFormFillAnalytics={() => {}}
                withSubscribeForMailing={true}
              />

              <Submit label={'Отправить'} loading={isSendingStatus} />
            </form>
          </FormProvider>
        </div>
      )}

      <FormNoticeBlock
        formStatus={formStatus}
        successNotice={formSuccessNotice}
        analyticsButtonId={''}
        errorNotice={formErrorNotice}
        setFormStatus={setFormStatus}
        formVariant={formVariant}
        showNotice={showNotice}
      />
    </div>
  );
}

export { Form };
